import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { Lock, ArrowRight } from "lucide-react";

export default function ResetPassword() {
  useSEO({
    title: "Set a new password — ErrandDay",
    description: "Finish your ErrandDay password reset and get back to managing your tasks.",
    noindex: true,
    canonicalPath: "/reset-password",
  });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { setErr("Password must be 6+ characters"); return; }
    if (pw !== pw2) { setErr("Passwords don't match"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      navigate("/login?reset=ok", { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md eb-fadein">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-md bg-[var(--eb-primary)] flex items-center justify-center text-white font-black">ED</div>
          <span className="font-[800] text-lg">ErrandDay</span>
        </Link>
        <span className="eb-chip">Choose a new password</span>
        <h2 className="mt-4 text-3xl">Set a new password</h2>
        <p className="mt-2 text-[var(--eb-text-dim)] text-sm">Make it at least 6 characters. You&apos;ll be signed in automatically after.</p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label className="eb-label">New password</label>
            <input data-testid="reset-pw-input" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="eb-input" placeholder="••••••••" />
          </div>
          <div>
            <label className="eb-label">Confirm</label>
            <input data-testid="reset-pw2-input" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="eb-input" placeholder="••••••••" />
          </div>
          {err && <div className="text-sm text-[var(--eb-secondary)]">{err}</div>}
          <button data-testid="reset-submit-btn" disabled={busy || !token} type="submit" className="eb-btn eb-btn-primary w-full !py-3">
            {busy ? "Saving…" : (<><Lock className="w-4 h-4" /> Save new password</>)}
          </button>
        </form>
      </div>
    </div>
  );
}
